import { h, p as proxyCustomElement, H, c as createEvent, F as Fragment } from './index.js';
import { g as getValidityObject, c as checkValidity, r as reportValidity, s as setCustomValidity, a as setInputRef, b as getError, w as watchValue, d as watchError, e as watchWarning, f as renderFormField } from './p-Cg8AdZPK.js';
import { k as konsole } from './p-YbMWREgz.js';
import { g as getBoxAttributes } from './p-CXN6CHBw.js';
import { d as defineCustomElement$6 } from './p-Cd2X-6KG.js';
import { d as defineCustomElement$5 } from './p-DZKBv4tL.js';
import { d as defineCustomElement$4 } from './p-aAvJ0uEW.js';
import { d as defineCustomElement$3 } from './p-Bjt5CD4D.js';
import { d as defineCustomElement$2 } from './p-QtR5VUMp.js';

// biome-ignore lint/correctness/noUnusedImports: needed for Stencil
const IncrementButton = ({ direction, disabled, onIncrement }) => (h("button", { type: "button", class: `increment-${direction}`, disabled: disabled, onClick: () => onIncrement(direction) }, h("wui-icon", { name: `chevron-${direction}`, size: "sm" })));

//#region src/module/format/duration/index.ts
const UNITS = [
	"w",
	"d",
	"h",
	"m",
	"s",
	"S"
];
const UNIT_NAMES = {
	w: "week",
	d: "day",
	h: "hour",
	m: "minute",
	s: "second",
	S: "millisecond"
};
function isTimeUnit(str) {
	if (typeof str === "undefined") return false;
	return UNITS.includes(str);
}
const CONVERSION = {
	w: {
		w: (n) => n,
		d: (n) => n * 7,
		h: (n) => n * 7 * 24,
		m: (n) => n * 7 * 24 * 60,
		s: (n) => n * 7 * 24 * 60 * 60,
		S: (n) => n * 7 * 24 * 60 * 60 * 1e3
	},
	d: {
		w: (n) => n / 7,
		d: (n) => n,
		h: (n) => n * 24,
		m: (n) => n * 24 * 60,
		s: (n) => n * 24 * 60 * 60,
		S: (n) => n * 24 * 60 * 60 * 1e3
	},
	h: {
		w: (n) => n / 24 / 7,
		d: (n) => n / 24,
		h: (n) => n,
		m: (n) => n * 60,
		s: (n) => n * 60 * 60,
		S: (n) => n * 60 * 60 * 1e3
	},
	m: {
		w: (n) => n / 60 / 24 / 7,
		d: (n) => n / 60 / 24,
		h: (n) => n / 60,
		m: (n) => n,
		s: (n) => n * 60,
		S: (n) => n * (60 * 1e3)
	},
	s: {
		w: (n) => n / 60 / 60 / 24 / 7,
		d: (n) => n / 60 / 60 / 24,
		h: (n) => n / 60 / 60,
		m: (n) => n / 60,
		s: (n) => n,
		S: (n) => n * 1e3
	},
	S: {
		w: (n) => n / 1e3 / 60 / 60 / 24 / 7,
		d: (n) => n / 1e3 / 60 / 60 / 24,
		h: (n) => n / 1e3 / 60 / 60,
		m: (n) => n / 1e3 / 60,
		s: (n) => n / 1e3,
		S: (n) => n
	}
};
function convertTime(num, fromUnit, toUnit) {
	if (!fromUnit) throw new Error("Missing unit!");
	if (toUnit === fromUnit) return num;
	return CONVERSION[fromUnit][toUnit](num);
}
function parseString(str) {
	const [hmsStr, msStr] = str.split(".");
	const ms = msStr ? Number.parseInt(msStr.trim(), 10) : 0;
	const hms = hmsStr.split(":").map((v) => {
		const num = Number.parseInt(v, 10);
		if (Number.isNaN(num)) throw new Error(`Could not convert ${v} to number`);
		return num;
	});
	return {
		h: hms[0],
		m: hms[1],
		s: hms[2],
		ms
	};
}
function stringToMs(str) {
	const { h, m, s, ms } = parseString(str);
	return convertTime(h, "h", "S") + convertTime(m, "m", "S") + convertTime(s, "s", "S") + ms;
}
const getDurations = (val, unit) => {
	return {
		w: convertTime(val, unit, "w"),
		d: convertTime(val, unit, "d"),
		h: convertTime(val, unit, "h"),
		m: convertTime(val, unit, "m"),
		s: convertTime(val, unit, "s"),
		S: convertTime(val, unit, "S")
	};
};
function combineDurations(values, includeUnits) {
	const ret = { ...values };
	ret.w = Math.floor(ret.w);
	if (includeUnits.includes("w") && ret.w >= 1) {
		ret.d -= convertTime(ret.w, "w", "d");
		ret.h -= convertTime(ret.w, "w", "h");
		ret.m -= convertTime(ret.w, "w", "m");
		ret.s -= convertTime(ret.w, "w", "s");
		ret.S -= convertTime(ret.w, "w", "S");
	}
	ret.d = Math.floor(ret.d);
	if (includeUnits.includes("d") && ret.d >= 1) {
		ret.h -= convertTime(ret.d, "d", "h");
		ret.m -= convertTime(ret.d, "d", "m");
		ret.s -= convertTime(ret.d, "d", "s");
		ret.S -= convertTime(ret.d, "d", "S");
	}
	ret.h = Math.floor(ret.h);
	if (includeUnits.includes("h") && ret.h >= 1) {
		ret.m -= convertTime(ret.h, "h", "m");
		ret.s -= convertTime(ret.h, "h", "s");
		ret.S -= convertTime(ret.h, "h", "S");
	}
	ret.m = Math.floor(ret.m);
	if (includeUnits.includes("m") && ret.m >= 1) {
		ret.s -= convertTime(ret.m, "m", "s");
		ret.S -= convertTime(ret.m, "m", "S");
	}
	ret.s = Math.floor(ret.s);
	if (includeUnits.includes("s") && ret.s >= 1) ret.S -= convertTime(ret.s, "s", "S");
	return ret;
}
function getUnitText(val, unit, short) {
	return val + (short ? unit : ` ${UNIT_NAMES[unit]}${val !== 1 ? "s" : ""}`);
}
function strToFormat(val, fromUnit, toFormat) {
	switch (toFormat) {
		case "HMS": return strToCustomFormat(val, fromUnit, "{hh}:{mm}:{ss}");
		case "HMSMS": return strToCustomFormat(val, fromUnit, "{hh}:{mm}:{ss}.{SSS}");
		case "HMS_SHORT": {
			const { h, m, s } = combineDurations(getDurations(val, fromUnit), [
				"h",
				"m",
				"s"
			]);
			const hString = h === 0 ? void 0 : h;
			const mString = h === 0 ? m : `${m}`.padStart(2, "0");
			const sString = `${s}`.padStart(2, "0");
			return [
				hString,
				mString,
				sString
			].filter((v) => typeof v !== "undefined").join(":");
		}
		case "TEXT_LONG":
		case "TEXT_SHORT": {
			const short = toFormat === "TEXT_SHORT";
			if (fromUnit === "S" && val < 1e3) return getUnitText(0, "s", short);
			const { S: _S,...durations } = combineDurations(getDurations(val, fromUnit), [
				"w",
				"d",
				"h",
				"m",
				"s"
			]);
			const ret = Object.keys(durations).map((unit) => {
				const val$1 = durations[unit];
				return val$1 >= 1 ? getUnitText(val$1, unit, short) : void 0;
			}).filter(Boolean);
			if (short) return ret.join(" ");
			if (ret.length > 1) {
				const last = ret.pop();
				return [ret.join(", "), last].join(" and ");
			}
			return ret.join(", ");
		}
	}
}
function strToCustomFormat(val, fromUnit, formatStr) {
	const foundUnits = (formatStr.match(/({(w_?|d_?|hh?_?|mm?_?|ss?_?|SS?S?_?)})/gm) ?? []).map((u) => {
		return u.replace("{", "").replace("}", "").replace("_", "").substring(0, 1);
	}).filter(Boolean);
	const durations = combineDurations(getDurations(val, fromUnit), foundUnits);
	return formatStr.replace(/({(hh|mm|ss|SS|SSS)})/g, (_match, _g1, g2) => {
		const unit = g2.substring(0, 1);
		return `${durations[unit]}`.padStart(g2.length, "0");
	}).replace(/({(w|d|h|m|s|S)})/g, (_match, _g1, g2) => {
		const unit = g2;
		return String(durations[unit]);
	}).replace(/({(w_|d_|h_|m_|s_|S_)})/g, (_match, _g1, g2) => {
		const unit = g2.replace("_", "");
		const val$1 = durations[unit];
		const name = UNIT_NAMES[unit];
		return `${val$1} ${name}${val$1 !== 1 ? "s" : ""}`;
	});
}
var Duration = class {
	value;
	unit;
	constructor(value, type) {
		this.value = value;
		if (typeof value === "number") if (isTimeUnit(type)) this.unit = type;
		else throw new Error("Invalid time unit passed!");
	}
	_getNumValue() {
		let numValue;
		let fromUnit;
		if (typeof this.value === "string") {
			numValue = stringToMs(this.value);
			fromUnit = "S";
		} else {
			if (!this.unit) throw new Error("Missing unit");
			numValue = this.value;
			fromUnit = this.unit;
		}
		return {
			numValue,
			fromUnit
		};
	}
	/**
	* Convert time to a number in a specific unit
	*/
	toNumber(toUnit = "s") {
		if (this.unit && toUnit === this.unit) return +this.value;
		if (typeof this.value === "number") return convertTime(this.value, this.unit, toUnit);
		return convertTime(stringToMs(this.value), "S", toUnit);
	}
	/**
	* Convert time to preset format
	*/
	toFormat(toFormat = "HMS") {
		const { numValue, fromUnit } = this._getNumValue();
		return strToFormat(numValue, fromUnit, toFormat);
	}
	toString(formatStr) {
		const { numValue, fromUnit } = this._getNumValue();
		return strToCustomFormat(numValue, fromUnit, formatStr);
	}
};

const MINUTES_SECONDS_MAX = 59;
const MS_MAX = 999;
const unitSpecs = {
    h: {
        max: Number.POSITIVE_INFINITY,
        charLimit: Number.POSITIVE_INFINITY,
    },
    m: {
        max: MINUTES_SECONDS_MAX,
        charLimit: 2,
    },
    s: {
        max: MINUTES_SECONDS_MAX,
        charLimit: 2,
    },
    ms: {
        max: MS_MAX,
        charLimit: 3,
    },
};
const splitUnits = (str) => str?.split(/[.:]+/) || [];
const inputsToMs = (inputs) => {
    const values = Object.values(inputs);
    if (values.every((value) => !value)) {
        // Every value is undefined, don't try to do math on it
        return;
    }
    const [h = 0, m = 0, s = 0, ms = 0] = values;
    return ((+h * 60 * 60 + +m * 60 + +s) * 1000 + +ms).toString();
};
const msToInputs = (milliseconds) => {
    if (!milliseconds) {
        return {
            h: undefined,
            m: undefined,
            s: undefined,
            ms: undefined,
        };
    }
    const hmsms = new Duration(Number(milliseconds), 'S').toFormat('HMSMS');
    const [h, m, s, ms] = splitUnits(hmsms);
    return { h, m, s, ms };
};
const msToHmsms = (ms, showMs) => {
    let _showMs = showMs;
    if (Number(ms) <= 999) {
        // 100 ms will look like 00:00:00 if showMs is false
        _showMs = true;
    }
    return new Duration(Number(ms), 'S').toFormat(_showMs ? 'HMSMS' : 'HMS');
};
const trimLeadingZeros = (value) => {
    if (typeof value === 'undefined' || value === '') {
        // Don't treat this like a number
        return '';
    }
    return Number(value).toString();
};
const increment = ({ direction, value, max, }) => {
    let number = Number(value);
    if (direction === 'up') {
        if ((max && +value < max) || !max) {
            number++;
        }
    }
    else {
        if (number > 0) {
            number--;
        }
    }
    return number.toString();
};
const getSmallestIncrement = (format) => {
    switch (format) {
        case 'hms':
        case 'ms':
            return '[data-unit=s]';
        case 'hm':
            return '[data-unit=m]';
        case 'hmsms':
        case 'msms':
            return '[data-unit=ms]';
        default:
            return '[data-unit=s]';
    }
};
const getFormatDescription = (format) => {
    switch (format) {
        case 'hms':
            return 'hh:mm:ss';
        case 'hm':
            return 'hh:mm';
        case 'ms':
            return 'mm:ss';
        case 'msms':
            return 'mm:ss.ms';
        case 'hmsms':
            return 'hh:mm:ss.ms';
        default:
            return 'hh:mm:ss';
    }
};

// biome-ignore lint/correctness/noUnusedImports: needed for Stencil
const DurationUnitInput = ({ unit, value, disabled, onFocus, onInput, onKeyDown, }) => {
    const { max, charLimit } = unitSpecs[unit];
    const charLength = value ? value.length : 2;
    // Inputs should be as wide as their character limit
    const minWidth = `${unit === 'h' ? 2 : charLimit}ch`;
    // 'hours' has no limit & stretches as needed
    const maxWidth = `${unit === 'h' ? charLength : charLimit}ch`;
    const handleFocus = (e) => {
        const input = e.target;
        input.select();
        onFocus(input);
    };
    return (h("input", { "data-unit": unit, type: "number", disabled: disabled, class: "input", style: { minWidth, maxWidth }, placeholder: unit === 'ms' ? '000' : '00', min: 0, max: max, onClick: handleFocus, onFocus: handleFocus, onInput: onInput, onKeyDown: onKeyDown, value: value }));
};

const durationInputCss = ":host{display:flex;position:relative;width:100%}:host .box{align-items:center;background-color:var(--jw-theme-form-field-default-bg-color);border:1px solid var(--jw-theme-form-border-regular-default-border-color);border-radius:.25rem;color:var(--jw-theme-form-field-filled-color);display:flex;font-family:Open Sans;font-size:.875rem;font-size:inherit;font-weight:400;font-weight:inherit;gap:.5rem;height:2rem;letter-spacing:-.0004em;line-height:1.25rem;min-width:64px;padding:0 .5rem;transition:border-color .3s ease;width:100%}:host .box[disabled]{background-color:var(--jw-theme-form-field-disabled-bg-color);color:var(--jw-theme-form-field-disabled-color);cursor:not-allowed}:host .box:not([disabled]):hover{border-color:var(--jw-theme-form-border-regular-hover-border-color)}:host .box:not([disabled]):focus,:host .box:not([disabled]):focus-within{border-color:var(--jw-theme-form-border-regular-focus-border-color);outline:var(--jw-theme-effects-focus-default-outline)}:host:focus,:host:focus-visible{outline:none}.inner{display:flex;width:100%}:host([is-empty]) .box{color:var(--jw-theme-form-field-placeholder-color)}:host([error-type=error]) .box{border-color:var(--jw-theme-form-border-error-default-border-color)}:host([error-type=error]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-error-hover-border-color)}:host([error-type=warning]) .box{border-color:var(--jw-theme-form-border-warning-default-border-color)}:host([error-type=warning]:not([disabled]):hover) .box{border-color:var(--jw-theme-form-border-warning-hover-border-color)}:host .input::placeholder{color:var(--jw-theme-form-field-placeholder-color)}:host .input::-ms-clear,:host .input::-webkit-clear-button,:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;display:none;margin:0}:host .input::-webkit-search-cancel-button,:host .input::-webkit-search-decoration,:host .input::-webkit-search-results-button,:host .input::-webkit-search-results-decoration{-webkit-appearance:none;appearance:none}:host .input::-webkit-calendar-picker-indicator{background:none;display:none}:host .input[type=number]{-webkit-appearance:textfield;appearance:textfield}:host .input::-webkit-inner-spin-button,:host .input::-webkit-outer-spin-button{-webkit-appearance:none;appearance:none;margin:0}:host .input:-webkit-autofill,:host .input:-webkit-autofill:first-line{font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem}:host .input:-webkit-autofill,:host .input:-webkit-autofill:focus,:host .input:-webkit-autofill:hover{box-shadow:0 0 0 1000px var(--jw-theme-form-field-default-bg-color) inset;-webkit-text-fill-color:var(--jw-theme-form-field-filled-color)}:host .input:-webkit-autofill[disabled]{box-shadow:0 0 0 1000px var(--jw-theme-form-field-disabled-bg-color) inset}:host .input[disabled]{cursor:not-allowed}:host .input[disabled]::placeholder{color:var(--jw-theme-surface-text-disabled-color)}:host .input{background:none;border:none;box-shadow:none;color:var(--jw-theme-form-field-filled-color);flex:1;float:left;font-family:Open Sans;font-size:.875rem;font-weight:400;height:100%;letter-spacing:-.0004em;line-height:1.25rem;margin:0;min-width:0;padding:0;width:100%}:host .input:focus,:host .input:focus-visible{outline:none}:host([is-empty]) .input{color:var(--jw-theme-form-field-placeholder-color)}:host([disabled]) .input{color:var(--jw-theme-form-field-disabled-color)}:host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;width:8rem}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}:host .box{justify-content:space-between;padding-right:.125rem;width:8rem}.inputs{align-items:center;display:flex}.separator{padding-bottom:2px}.controls{display:grid;gap:.125rem}.controls button{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;align-items:center;background:none;background-color:var(--jw-theme-form-addon-default-bg-color);border:none;border-radius:.125rem;box-sizing:border-box;color:var(--jw-theme-form-addon-default-color);cursor:pointer;display:inline-flex;display:flex;flex:1;font-family:Open Sans;font-size:.875rem;font-weight:400;font-weight:inherit;height:.75rem;justify-content:center;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal;margin:0;padding:0;transition:color .1s ease,background-color .1s ease;-webkit-user-select:none;user-select:none;width:.75rem}.controls button *,.controls button :after,.controls button :before{box-sizing:inherit}.controls button img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}.controls button[disabled]{cursor:not-allowed}.controls button:focus{outline:none}.controls button wui-icon{height:.5rem;width:.5rem}.controls button:not([disabled]):hover{background-color:var(--jw-theme-form-addon-hover-bg-color);color:var(--jw-theme-form-addon-hover-color)}.controls button:not([disabled]):focus{background-color:var(--jw-theme-form-addon-active-bg-color);color:var(--jw-theme-form-addon-active-color)}.controls button[disabled]{background-color:var(--jw-theme-form-field-disabled-inverted-bg-color);color:var(--jw-theme-form-field-disabled-inverted-color)}";

const WuiDurationInput$1 = /*@__PURE__*/ proxyCustomElement(class WuiDurationInput extends H {
    constructor(registerHost) {
        super();
        if (registerHost !== false) {
            this.__registerHost();
        }
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.required = false;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        //////// FORM FIELD END ////////
        /**
         * Input name
         */
        this.name = undefined;
        /**
         * If disabled
         */
        this.disabled = false;
        /**
         * Duration input format, defaults to 'hms'
         */
        this.format = 'hms';
        /**
         * Minimum value in milliseconds
         */
        this.min = undefined;
        /**
         * Maximum value in milliseconds
         */
        this.max = undefined;
        /**
         * Current/most recent input in focus
         */
        this.focusedInput = undefined;
        /**
         * Last key pressed
         */
        this.lastKey = undefined;
        this.rawValues = {
            h: undefined,
            m: undefined,
            s: undefined,
            ms: undefined,
        };
        this.setValue = (inputValues) => {
            this.value = inputValues ? inputsToMs(inputValues) : undefined;
            this.dirty = true;
            this.checkValidity();
            this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        };
        this.setLastFocused = (el) => {
            this.focusedInput = el;
            el?.select();
        };
        this.handleKeyDown = (e) => {
            e.stopPropagation();
            const { key } = e;
            const usedLeftRightArrowKeys = key === 'ArrowLeft' || key === 'ArrowRight';
            const input = this.focusedInput;
            if (input && usedLeftRightArrowKeys) {
                // Allow user to tab through inputs with arrow keys
                const direction = key === 'ArrowLeft' ? 'prev' : 'next';
                e.preventDefault();
                this.autoTab(input, direction);
            }
            this.lastKey = key;
        };
        this.incrementWithButton = (direction) => {
            // If nothing is focused, start with the smallest increment
            const smallestIncrement = getSmallestIncrement(this.format);
            const elm = this.focusedInput || this.elm.shadowRoot?.querySelector(smallestIncrement);
            if (!elm) {
                throw new Error('Can not find focused input');
            }
            const unit = elm.dataset.unit;
            const { max } = unitSpecs[unit];
            let { value } = elm;
            value = increment({ direction, value, max });
            this.rawValues[unit] = value;
            this.setValue({ ...msToInputs(this.value), [unit]: value });
        };
        this.setUnit = (e) => {
            // Prevent inner event from bubbling up
            e.stopPropagation();
            // Prevent double events firing alongside onKeyDown
            e.preventDefault();
            const usedUpDownArrowKeys = this.lastKey === 'ArrowUp' || this.lastKey === 'ArrowDown';
            const elm = e.target;
            const unit = elm.dataset.unit;
            const { charLimit, max } = unitSpecs[unit];
            let { value } = elm;
            this.rawValues[unit] = value;
            if (value && !usedUpDownArrowKeys) {
                // We only care about charLimit and min/max if user is typing numbers
                const charCount = trimLeadingZeros(value).length;
                if (charCount === charLimit) {
                    // Value is good, jump to next input
                    this.autoTab(elm);
                }
                if (Number(value) > max) {
                    // Don't allow, reset to max
                    value = max.toString();
                }
                if (charCount > charLimit || Number(value) < 0) {
                    // Don't allow, reset to zero
                    value = '0';
                }
            }
            const isEmpty = Object.values(this.rawValues).every((v) => v === '' || v === undefined);
            // Set the value
            this.setValue(isEmpty ? undefined : { ...msToInputs(this.value), [unit]: value });
        };
        this.autoTab = (currentElement, dir = 'next') => {
            let sibling = dir === 'prev' ? currentElement.previousElementSibling : currentElement.nextElementSibling;
            let focus;
            while (sibling) {
                if (sibling.classList.contains('input')) {
                    focus = sibling;
                    break;
                }
                sibling = dir === 'prev' ? sibling.previousElementSibling : sibling.nextElementSibling;
            }
            if (!focus) {
                // Restart at hours if no next sibling
                const hoursInput = this.elm.shadowRoot?.querySelector('[data-unit=h]');
                if (hoursInput) {
                    focus = hoursInput;
                }
            }
            this.setLastFocused(focus);
        };
    }
    _handleValueChange(newValue) {
        watchValue(this, newValue);
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    componentWillLoad() {
        if (this.min && this.max && this.min > this.max) {
            konsole.warn.component('DurationInput', 'Min and max values are incompatible.');
        }
    }
    renderInner() {
        const inputs = msToInputs(this.value);
        const showH = this.format.startsWith('h');
        const showS = this.format === 'hms' || this.format === 'ms' || this.format === 'msms';
        const showMs = this.format === 'hmsms' || this.format === 'msms';
        return (h(Fragment, null, h("input", { hidden: true, type: "number", ref: this.setInputRef, name: this.name, min: this.min, max: this.max, disabled: this.disabled, required: !!this.required, value: this.value, onInvalid: (e) => {
                const elm = e.currentTarget;
                if (!elm) {
                    return;
                }
                elm.setCustomValidity('');
                // Use custom error messages for min and max errors
                const { rangeOverflow, rangeUnderflow } = elm.validity;
                if (this.min && rangeUnderflow) {
                    elm.setCustomValidity(`Value must be greater than ${msToHmsms(this.min, showMs)}`);
                }
                else if (this.max && rangeOverflow) {
                    elm.setCustomValidity(`Value must be less than ${msToHmsms(this.max, showMs)}`);
                }
            } }), h("div", { class: "box", ...getBoxAttributes(this) }, h("div", { class: "inputs" }, showH && (h(Fragment, null, h(DurationUnitInput, { unit: "h", disabled: this.disabled, onFocus: this.setLastFocused, onInput: this.setUnit, onKeyDown: this.handleKeyDown, value: this.rawValues.h === '' ? undefined : inputs.h }), h("div", { class: "separator" }, ":"))), h(DurationUnitInput, { unit: "m", disabled: this.disabled, onFocus: this.setLastFocused, onInput: this.setUnit, onKeyDown: this.handleKeyDown, value: this.rawValues.m === '' ? undefined : inputs.m }), (showS || showMs) && (h(Fragment, null, h("div", { class: "separator" }, ":"), h(DurationUnitInput, { unit: "s", disabled: this.disabled, onFocus: this.setLastFocused, onInput: this.setUnit, onKeyDown: this.handleKeyDown, value: this.rawValues.s === '' ? undefined : inputs.s }))), showMs && (h(Fragment, null, h("div", { class: "separator" }, "."), h(DurationUnitInput, { unit: "ms", disabled: this.disabled, onFocus: this.setLastFocused, onInput: this.setUnit, onKeyDown: this.handleKeyDown, value: this.rawValues.ms === '' ? undefined : inputs.ms })))), h("div", { class: "controls" }, h(IncrementButton, { direction: "up", onIncrement: this.incrementWithButton, disabled: this.disabled }), h(IncrementButton, { direction: "down", onIncrement: this.incrementWithButton, disabled: this.disabled })))));
    }
    render() {
        return renderFormField(this, this.renderInner(), { formField: { description: getFormatDescription(this.format) } });
    }
    get elm() { return this; }
    static get watchers() { return {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return durationInputCss; }
}, [257, "wui-duration-input", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16, "check-validity"],
        "reportValidity": [16, "report-validity"],
        "setCustomValidity": [16, "set-custom-validity"],
        "formField": [16, "form-field"],
        "name": [1],
        "disabled": [516],
        "format": [1],
        "min": [1],
        "max": [1],
        "customValidation": [32],
        "showValidity": [32]
    }, undefined, {
        "value": ["_handleValueChange"],
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-duration-input", "wui-box", "wui-form-field", "wui-icon", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-duration-input":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDurationInput$1);
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiDurationInput = WuiDurationInput$1;
const defineCustomElement = defineCustomElement$1;

export { WuiDurationInput, defineCustomElement };
